import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MapPin, GitCommit, Package, Clock, ExternalLink, RefreshCw, Globe, Plus, Minus, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';

// TopoJSON for world map
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ============================================================================
// CONFIGURATION - The only things you update manually
// ============================================================================
const CONFIG = {
  github: {
    username: 'josharsh',
    featuredRepos: ['mcp-jest', '100LinesOfCode', 'omnimcp'],
  },
  npm: {
    packages: ['mcp-jest', 'ai-terminal-course', 'genai-hooks', 'naturalgit'],
  },
  location: 'Bangalore, India',
  timezone: 'Asia/Kolkata',
  // The only manual update - what you're focused on
  currentFocus: "Building infrastructure for AI agents that actually work in production",
  careerStartDate: '2018-01-15', // For calculating years
};

// ============================================================================
// FONT LOADER
// ============================================================================
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Newsreader:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Caveat:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

    .font-editorial { font-family: 'Newsreader', Georgia, serif; }
    .font-handwritten { font-family: 'Caveat', cursive; }
    .font-mono { font-family: 'JetBrains Mono', monospace; }

    /* Sketchy underline */
    .sketchy-underline {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 12'%3E%3Cpath d='M2 8 Q 25 2, 50 8 T 98 6' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: bottom;
      background-size: 100% 8px;
      padding-bottom: 4px;
    }

    /* Paper texture */
    .paper-texture {
      background-color: #faf9f6;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
      background-blend-mode: soft-light;
    }

    /* Rough border effect */
    .rough-border {
      border-radius: 255px 15px 225px 15px/15px 225px 15px 255px;
    }

    /* Ink blob animation */
    @keyframes inkSpread {
      0% { transform: scale(0); opacity: 0; }
      50% { opacity: 0.1; }
      100% { transform: scale(1); opacity: 0.05; }
    }
  `}</style>
);

// ============================================================================
// GITHUB API HOOKS
// ============================================================================
interface GitHubEvent {
  type: string;
  repo: { name: string };
  created_at: string;
  payload: any;
}

interface GitHubStats {
  lastCommit: { repo: string; message: string; date: string } | null;
  recentActivity: GitHubEvent[];
  totalRepos: number;
  loading: boolean;
  error: string | null;
}

const useGitHubStats = (username: string): GitHubStats => {
  const [stats, setStats] = useState<GitHubStats>({
    lastCommit: null,
    recentActivity: [],
    totalRepos: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const fetchGitHubData = async () => {
      try {
        // Fetch recent events
        const eventsRes = await fetch(`https://api.github.com/users/${username}/events/public?per_page=30`);
        if (!eventsRes.ok) throw new Error('GitHub API rate limited');
        const events: GitHubEvent[] = await eventsRes.json();

        // Find last push event with commits
        const pushEvents = events.filter(e => e.type === 'PushEvent');
        const lastPush = pushEvents[0];

        let lastCommit = null;
        if (lastPush && lastPush.payload.commits?.length > 0) {
          const commit = lastPush.payload.commits[lastPush.payload.commits.length - 1];
          lastCommit = {
            repo: lastPush.repo.name.split('/')[1],
            message: commit.message.split('\n')[0].substring(0, 60),
            date: lastPush.created_at,
          };
        }

        // Fetch user info for repo count
        const userRes = await fetch(`https://api.github.com/users/${username}`);
        const userData = await userRes.json();

        setStats({
          lastCommit,
          recentActivity: events.slice(0, 10),
          totalRepos: userData.public_repos || 0,
          loading: false,
          error: null,
        });
      } catch (err) {
        setStats(prev => ({ ...prev, loading: false, error: 'Could not fetch live data' }));
      }
    };

    fetchGitHubData();
  }, [username]);

  return stats;
};

// ============================================================================
// NPM API HOOKS
// ============================================================================
interface NpmStats {
  totalDownloads: number;
  packages: { name: string; downloads: number }[];
  loading: boolean;
}

const useNpmStats = (packageNames: string[]): NpmStats => {
  const [stats, setStats] = useState<NpmStats>({
    totalDownloads: 0,
    packages: [],
    loading: true,
  });

  useEffect(() => {
    const fetchNpmData = async () => {
      try {
        const packageStats = await Promise.all(
          packageNames.map(async (name) => {
            try {
              const res = await fetch(`https://api.npmjs.org/downloads/point/last-month/${name}`);
              const data = await res.json();
              return { name, downloads: data.downloads || 0 };
            } catch {
              return { name, downloads: 0 };
            }
          })
        );

        const total = packageStats.reduce((sum, p) => sum + p.downloads, 0);
        setStats({
          totalDownloads: total,
          packages: packageStats.sort((a, b) => b.downloads - a.downloads),
          loading: false,
        });
      } catch {
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchNpmData();
  }, [packageNames.join(',')]);

  return stats;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const getTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getYearsSince = (dateString: string): number => {
  const start = new Date(dateString);
  const now = new Date();
  return Math.floor((now.getTime() - start.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
};

const formatNumber = (num: number): string => {
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toString();
};

// ============================================================================
// WORLD MAP - Geographic data for developer populations (lat/lng)
// ============================================================================

interface GeoLocation {
  name: string;
  coordinates: [number, number]; // [longitude, latitude]
  weight: number; // probability weight
}

// Major developer hubs with realistic distribution - using actual coordinates
const DEVELOPER_HUBS: GeoLocation[] = [
  // USA
  { name: 'San Francisco', coordinates: [-122.4194, 37.7749], weight: 12 },
  { name: 'New York', coordinates: [-74.006, 40.7128], weight: 8 },
  { name: 'Seattle', coordinates: [-122.3321, 47.6062], weight: 5 },
  { name: 'Austin', coordinates: [-97.7431, 30.2672], weight: 4 },
  { name: 'Boston', coordinates: [-71.0589, 42.3601], weight: 3 },
  { name: 'Los Angeles', coordinates: [-118.2437, 34.0522], weight: 4 },
  // Europe
  { name: 'London', coordinates: [-0.1276, 51.5074], weight: 8 },
  { name: 'Berlin', coordinates: [13.405, 52.52], weight: 5 },
  { name: 'Amsterdam', coordinates: [4.9041, 52.3676], weight: 4 },
  { name: 'Paris', coordinates: [2.3522, 48.8566], weight: 4 },
  { name: 'Stockholm', coordinates: [18.0686, 59.3293], weight: 2 },
  // India
  { name: 'Bangalore', coordinates: [77.5946, 12.9716], weight: 10 },
  { name: 'Mumbai', coordinates: [72.8777, 19.076], weight: 5 },
  { name: 'Delhi', coordinates: [77.1025, 28.7041], weight: 4 },
  { name: 'Hyderabad', coordinates: [78.4867, 17.385], weight: 3 },
  { name: 'Chennai', coordinates: [80.2707, 13.0827], weight: 2 },
  { name: 'Pune', coordinates: [73.8567, 18.5204], weight: 2 },
  // China & East Asia
  { name: 'Beijing', coordinates: [116.4074, 39.9042], weight: 4 },
  { name: 'Shanghai', coordinates: [121.4737, 31.2304], weight: 4 },
  { name: 'Shenzhen', coordinates: [114.0579, 22.5431], weight: 3 },
  { name: 'Tokyo', coordinates: [139.6917, 35.6895], weight: 4 },
  { name: 'Singapore', coordinates: [103.8198, 1.3521], weight: 3 },
  { name: 'Seoul', coordinates: [126.978, 37.5665], weight: 3 },
  // South America
  { name: 'São Paulo', coordinates: [-46.6333, -23.5505], weight: 3 },
  { name: 'Buenos Aires', coordinates: [-58.3816, -34.6037], weight: 2 },
  // Australia
  { name: 'Sydney', coordinates: [151.2093, -33.8688], weight: 2 },
  { name: 'Melbourne', coordinates: [144.9631, -37.8136], weight: 1 },
  // Other
  { name: 'Toronto', coordinates: [-79.3832, 43.6532], weight: 2 },
  { name: 'Tel Aviv', coordinates: [34.7818, 32.0853], weight: 2 },
  { name: 'Dubai', coordinates: [55.2708, 25.2048], weight: 2 },
];

// Calculate total weight for probability distribution
const TOTAL_WEIGHT = DEVELOPER_HUBS.reduce((sum, hub) => sum + hub.weight, 0);

// Pick a random location based on weights
const getRandomLocation = (): GeoLocation => {
  let random = Math.random() * TOTAL_WEIGHT;
  for (const hub of DEVELOPER_HUBS) {
    random -= hub.weight;
    if (random <= 0) return hub;
  }
  return DEVELOPER_HUBS[0];
};

// Get a color for each package
const PACKAGE_COLORS: Record<string, string> = {
  'mcp-jest': '#06b6d4',      // cyan
  'ai-terminal-course': '#8b5cf6', // purple
  'genai-hooks': '#22c55e',    // green
  'naturalgit': '#f97316',     // orange
  'omnimcp': '#ec4899',        // pink
};

interface DownloadPing {
  id: string;
  coordinates: [number, number];
  location: string;
  package: string;
  color: string;
}

// ============================================================================
// DOWNLOAD MAP COMPONENT - Using react-simple-maps
// ============================================================================

// Memoized map background to prevent re-renders
const MapBackground = memo(() => (
  <Geographies geography={GEO_URL}>
    {({ geographies }) =>
      geographies.map((geo) => (
        <Geography
          key={geo.rsmKey}
          geography={geo}
          fill="#f1f5f9"
          stroke="#e2e8f0"
          strokeWidth={0.5}
          style={{
            default: { outline: 'none' },
            hover: { outline: 'none' },
            pressed: { outline: 'none' },
          }}
        />
      ))
    }
  </Geographies>
));

const DownloadMap: React.FC<{ packages: { name: string; downloads: number }[], loading: boolean }> = ({ packages, loading }) => {
  const [pings, setPings] = useState<DownloadPing[]>([]);
  const pingIdCounter = useRef(0);

  // Zoom state
  const [position, setPosition] = useState({ coordinates: [20, 20] as [number, number], zoom: 1 });

  const handleZoomIn = () => {
    if (position.zoom >= 4) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom * 1.5 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 1) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom / 1.5 }));
  };

  const handleReset = () => {
    setPosition({ coordinates: [20, 20], zoom: 1 });
  };

  const handleMoveEnd = (position: { coordinates: [number, number]; zoom: number }) => {
    setPosition(position);
  };

  // Calculate downloads per second across all packages
  const totalMonthlyDownloads = packages.reduce((sum, p) => sum + p.downloads, 0);
  const downloadsPerSecond = totalMonthlyDownloads / (30 * 24 * 60 * 60);

  // Create pings at realistic intervals
  useEffect(() => {
    if (loading || packages.length === 0) return;

    // Speed up for visual effect - aim for 1-2 pings per second
    const visualMultiplier = Math.max(1, 1.5 / Math.max(downloadsPerSecond, 0.001));
    const intervalMs = Math.max(500, Math.min(2000, 1000 / (downloadsPerSecond * visualMultiplier)));

    const addPing = () => {
      const location = getRandomLocation();

      // Pick a random package weighted by downloads
      const totalDl = packages.reduce((s, p) => s + p.downloads, 0);
      let rand = Math.random() * totalDl;
      let selectedPackage = packages[0];
      for (const pkg of packages) {
        rand -= pkg.downloads;
        if (rand <= 0) {
          selectedPackage = pkg;
          break;
        }
      }

      // Add slight jitter to coordinates
      const jitterLng = (Math.random() - 0.5) * 4;
      const jitterLat = (Math.random() - 0.5) * 3;

      const newPing: DownloadPing = {
        id: `ping-${pingIdCounter.current++}`,
        coordinates: [
          location.coordinates[0] + jitterLng,
          location.coordinates[1] + jitterLat
        ],
        location: location.name,
        package: selectedPackage.name,
        color: PACKAGE_COLORS[selectedPackage.name] || '#3b82f6',
      };

      setPings(prev => [...prev.slice(-20), newPing]);
    };

    // Start with a few pings
    for (let i = 0; i < 4; i++) {
      setTimeout(() => addPing(), i * 300);
    }

    const interval = setInterval(addPing, intervalMs);
    return () => clearInterval(interval);
  }, [loading, packages, downloadsPerSecond]);

  // Cleanup old pings
  useEffect(() => {
    const cleanup = setInterval(() => {
      setPings(prev => prev.slice(-15));
    }, 4000);
    return () => clearInterval(cleanup);
  }, []);

  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-gray-200/50 bg-[#f8fafc]">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 120,
        }}
        style={{ width: '100%', height: 'auto' }}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={handleMoveEnd}
          minZoom={1}
          maxZoom={4}
        >
          <MapBackground />

          {/* Static hub indicators */}
          {DEVELOPER_HUBS.map((hub) => (
            <Marker key={hub.name} coordinates={hub.coordinates}>
              <circle r={2 / position.zoom} fill="#d1d5db" opacity={0.4} />
            </Marker>
          ))}

          {/* Animated pings */}
          {pings.map((ping) => (
            <Marker key={ping.id} coordinates={ping.coordinates}>
              {/* Ripple effect */}
              <motion.circle
                initial={{ r: 2 / position.zoom, opacity: 0.8 }}
                animate={{ r: 20 / position.zoom, opacity: 0 }}
                transition={{ duration: 2.5, ease: "easeOut" }}
                fill={ping.color}
              />
              {/* Center dot */}
              <motion.circle
                initial={{ r: 0 }}
                animate={{ r: 4 / position.zoom }}
                exit={{ r: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                fill={ping.color}
              />
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>

      {/* Zoom Controls */}
      <div className="absolute top-3 left-3 flex flex-col gap-1">
        <button
          onClick={handleZoomIn}
          disabled={position.zoom >= 4}
          className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm flex items-center justify-center text-gray-600 hover:bg-white hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          title="Zoom in"
        >
          <Plus className="w-4 h-4" />
        </button>
        <button
          onClick={handleZoomOut}
          disabled={position.zoom <= 1}
          className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm flex items-center justify-center text-gray-600 hover:bg-white hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          title="Zoom out"
        >
          <Minus className="w-4 h-4" />
        </button>
        {position.zoom > 1 && (
          <button
            onClick={handleReset}
            className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm flex items-center justify-center text-gray-600 hover:bg-white hover:text-gray-900 transition-all"
            title="Reset view"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
        {packages.slice(0, 4).map((pkg) => (
          <div key={pkg.name} className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs shadow-sm">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: PACKAGE_COLORS[pkg.name] || '#3b82f6' }}
            />
            <span className="font-mono text-gray-700">{pkg.name}</span>
          </div>
        ))}
      </div>

      {/* Live indicator */}
      <div className="absolute top-3 right-3 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600"></span>
        </span>
        <span className="text-xs font-mono text-gray-600">
          ~{Math.max(downloadsPerSecond, 0.01).toFixed(2)}/sec
        </span>
      </div>

      {/* Zoom hint */}
      <div className="absolute bottom-3 right-3 text-[10px] text-gray-400 font-mono bg-white/70 px-2 py-1 rounded">
        drag to pan • scroll to zoom
      </div>

      {/* Loading state */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="flex items-center gap-2 text-gray-500">
            <Globe className="w-5 h-5 animate-pulse" />
            <span className="font-editorial">Loading download data...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTS
// ============================================================================

const SketchyBox: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`relative ${className}`}>
    {/* Hand-drawn border effect using SVG */}
    <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
      <rect
        x="2" y="2"
        width="calc(100% - 4px)" height="calc(100% - 4px)"
        fill="none"
        stroke="#d1d5db"
        strokeWidth="1.5"
        rx="3"
        style={{
          strokeDasharray: '8 4',
          strokeLinecap: 'round',
        }}
      />
    </svg>
    <div className="relative p-6">
      {children}
    </div>
  </div>
);

const LiveDot = () => (
  <span className="relative flex h-2 w-2">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600"></span>
  </span>
);

const HandwrittenNote: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <span className={`font-handwritten text-gray-500 ${className}`}>
    {children}
  </span>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const Now: React.FC = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Real data from APIs
  const githubStats = useGitHubStats(CONFIG.github.username);
  const npmStats = useNpmStats(CONFIG.npm.packages);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const localTime = currentTime.toLocaleTimeString('en-US', {
    timeZone: CONFIG.timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
  };

  const yearsBuilding = getYearsSince(CONFIG.careerStartDate);

  return (
    <div className="min-h-screen paper-texture">
      <FontLoader />

      {/* Subtle ink blob decorations */}
      <div className="fixed top-20 right-20 w-64 h-64 rounded-full bg-blue-200 opacity-[0.03] blur-3xl pointer-events-none" />
      <div className="fixed bottom-40 left-10 w-96 h-96 rounded-full bg-amber-200 opacity-[0.05] blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#faf9f6]/80 backdrop-blur-sm border-b border-gray-200/50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors font-editorial"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">back home</span>
          </button>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <LiveDot />
              <span className="font-mono text-xs">pulling live data</span>
            </div>
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-16">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <h1 className="font-editorial text-5xl md:text-6xl text-gray-900 mb-6 leading-tight">
            What I'm doing{' '}
            <span className="sketchy-underline italic">now</span>
          </h1>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span className="font-editorial">{CONFIG.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="font-mono">{localTime}</span>
            </div>
          </div>
        </motion.div>

        {/* Current Focus */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-16"
        >
          <SketchyBox>
            <div className="flex items-start justify-between mb-4">
              <HandwrittenNote className="text-xl">right now, obsessed with...</HandwrittenNote>
            </div>
            <p className="font-editorial text-2xl md:text-3xl text-gray-900 leading-relaxed">
              {CONFIG.currentFocus}
            </p>
          </SketchyBox>
        </motion.section>

        {/* Live Stats from APIs */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <HandwrittenNote className="text-lg block mb-6">the numbers (live from APIs)</HandwrittenNote>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Years Building */}
            <div className="p-4 bg-white/50 rounded-lg border border-gray-200/50">
              <div className="font-mono text-3xl text-gray-900">{yearsBuilding}</div>
              <div className="text-xs text-gray-500 mt-1">years building</div>
            </div>

            {/* GitHub Repos */}
            <div className="p-4 bg-white/50 rounded-lg border border-gray-200/50">
              <div className="font-mono text-3xl text-gray-900">
                {githubStats.loading ? '...' : githubStats.totalRepos}
              </div>
              <div className="text-xs text-gray-500 mt-1">public repos</div>
            </div>

            {/* NPM Downloads */}
            <div className="p-4 bg-white/50 rounded-lg border border-gray-200/50">
              <div className="font-mono text-3xl text-gray-900">
                {npmStats.loading ? '...' : formatNumber(npmStats.totalDownloads)}
              </div>
              <div className="text-xs text-gray-500 mt-1">npm downloads/mo</div>
            </div>

            {/* Local Time */}
            <div className="p-4 bg-white/50 rounded-lg border border-gray-200/50">
              <div className="font-mono text-3xl text-gray-900">
                {currentTime.toLocaleTimeString('en-US', { timeZone: CONFIG.timezone, hour: '2-digit', minute: '2-digit', hour12: false })}
              </div>
              <div className="text-xs text-gray-500 mt-1">local time (IST)</div>
            </div>
          </div>
        </motion.section>

        {/* npm Download Map - Live Visualization */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-6">
            <HandwrittenNote className="text-lg">where my code is running (live)</HandwrittenNote>
            <div className="text-xs text-gray-400 font-mono">
              {formatNumber(npmStats.totalDownloads)} downloads this month
            </div>
          </div>

          <DownloadMap packages={npmStats.packages} loading={npmStats.loading} />

          <p className="text-xs text-gray-400 mt-3 text-center font-editorial italic">
            Simulated distribution based on real npm download counts and global developer population
          </p>
        </motion.section>

        {/* Last Commit - Real from GitHub */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mb-16"
        >
          <HandwrittenNote className="text-lg block mb-6">last thing I shipped</HandwrittenNote>

          {githubStats.loading ? (
            <div className="p-6 bg-white/50 rounded-lg border border-gray-200/50 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-100 rounded w-1/2"></div>
            </div>
          ) : githubStats.lastCommit ? (
            <a
              href={`https://github.com/${CONFIG.github.username}/${githubStats.lastCommit.repo}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-6 bg-white/50 rounded-lg border border-gray-200/50 hover:border-gray-300 hover:bg-white/80 transition-all group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 mb-2">
                  <GitCommit className="w-5 h-5 text-gray-400" />
                  <span className="font-mono text-sm text-gray-500">{githubStats.lastCommit.repo}</span>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </div>
              <p className="font-editorial text-lg text-gray-900">
                "{githubStats.lastCommit.message}"
              </p>
              <p className="text-sm text-gray-400 mt-2 font-mono">
                {getTimeAgo(githubStats.lastCommit.date)}
              </p>
            </a>
          ) : (
            <div className="p-6 bg-white/50 rounded-lg border border-gray-200/50 text-gray-400">
              No recent commits found
            </div>
          )}
        </motion.section>

        {/* NPM Packages - Real Downloads */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-16"
        >
          <HandwrittenNote className="text-lg block mb-6">packages people are using</HandwrittenNote>

          <div className="space-y-3">
            {npmStats.loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-white/30 rounded-lg animate-pulse" />
              ))
            ) : (
              npmStats.packages.map((pkg, index) => (
                <motion.a
                  key={pkg.name}
                  href={`https://www.npmjs.com/package/${pkg.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-white/50 rounded-lg border border-gray-200/50 hover:border-gray-300 hover:bg-white/80 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-gray-400" />
                    <span className="font-mono text-gray-900">{pkg.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-gray-500">
                      {formatNumber(pkg.downloads)}/mo
                    </span>
                    <ExternalLink className="w-3 h-3 text-gray-300 group-hover:text-gray-500 transition-colors" />
                  </div>
                </motion.a>
              ))
            )}
          </div>
        </motion.section>

        {/* Recent GitHub Activity */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mb-16"
        >
          <HandwrittenNote className="text-lg block mb-6">recent activity (from GitHub)</HandwrittenNote>

          <div className="space-y-1">
            {githubStats.loading ? (
              <div className="text-gray-400 font-editorial">Loading activity...</div>
            ) : (
              githubStats.recentActivity.slice(0, 8).map((event, index) => {
                const eventTypes: Record<string, string> = {
                  PushEvent: 'pushed to',
                  CreateEvent: 'created',
                  IssuesEvent: 'issue on',
                  PullRequestEvent: 'PR on',
                  WatchEvent: 'starred',
                  ForkEvent: 'forked',
                  IssueCommentEvent: 'commented on',
                };
                const action = eventTypes[event.type] || event.type.replace('Event', '').toLowerCase();
                const repo = event.repo.name.split('/')[1];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 + index * 0.05 }}
                    className="flex items-center gap-4 py-2 text-sm border-b border-gray-100 last:border-0"
                  >
                    <span className="font-mono text-xs text-gray-400 w-16">
                      {getTimeAgo(event.created_at)}
                    </span>
                    <span className="text-gray-500">{action}</span>
                    <span className="font-mono text-gray-700">{repo}</span>
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.section>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.65 }}
          className="pt-12 border-t border-gray-200/50"
        >
          <div className="text-center">
            <HandwrittenNote className="text-base block mb-4">
              inspired by Derek Sivers' /now page movement
            </HandwrittenNote>
            <p className="text-xs text-gray-400 font-mono mb-6">
              Data pulled live from GitHub & npm APIs
            </p>
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-900 transition-colors font-editorial"
            >
              ← back to homepage
            </button>
          </div>
        </motion.footer>
      </main>
    </div>
  );
};

export default Now;
